import { action, computed, observable, reaction } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { LoyaltyRepository } from '~repositories/LoyaltyRepository';
import { LoadLoyaltyInfoUseCase } from '~use-cases/LoadLoyaltyInfoUseCase';
import { ILoyaltyLayouts } from '~views/components/interfaces';
import { LoyaltyLandingConfig } from './ILoyaltyLandingPage';
import { createMultiGridLayout } from '~utils/loadOnDemand';
import { LoyaltyDashboardLoadedUseCase } from '~use-cases/LoyaltyDashboardLoadedUseCase';
import mergeConfigurationFiles from '~utils/mergeConfigurationFiles';
import { ISiteConfig, IDrupalConfig, ILoyaltyConfig } from '~domain/entities/ISiteConfig';

export interface ILoyaltyLandingPageController {
    loyaltyRepository: LoyaltyRepository;
}

class LoyaltyLandingPageController extends ViewController {
    @observable private configs: LoyaltyLandingConfig;
    @observable private layouts: ILoyaltyLayouts;
    @observable private loyaltyRepository: LoyaltyRepository;

    constructor({ loyaltyRepository }: ILoyaltyLandingPageController) {
        super();
        this.loyaltyRepository = loyaltyRepository;
        reaction(
            () => this.loyaltyRepository?.loyaltyInfo?.hasLoyalty,
            () => {
                this.updateLayout();
            }
        );
    }

    @action
    public hasBirthdaySection = () => {
        return this.configs.birthday ? 1 : 0;
    };

    @action
    public getBirthdayOffersQueryFromParams = () => {
        return this.configs.birthday?.params?.birthday?.birthdayOfferType?.text;
    };
    @action
    private async fetchBirthdayDataIfNeeded(): Promise<void> {
        if (!this.hasBirthdaySection()) {
            return;
        }

        const query = this.getBirthdayOffersQueryFromParams();

        if (!query) {
            return;
        }

        await this.loyaltyRepository.fetchBirthdayData(query);
    }

    @action
    public loaded = async () => {
        this.isLoading = true;
        await this.loadConfig();
        await Promise.all([
            new LoyaltyDashboardLoadedUseCase().execute(),
            new LoadLoyaltyInfoUseCase().execute()
        ]);
        await this.fetchBirthdayDataIfNeeded();
        this.isLoading = false;
    };

    @action
    public async loadConfig(): Promise<void> {
        try {
            const loyaltyConfig = (window?.site as ISiteConfig)?.loyaltyConfig?.loyalty;

            const [drupalConfigData, loyaltyConfigData] = await Promise.all([
                import(`../../config/${loyaltyConfig.domain}-drupal.json`),
                import(`../../config/${loyaltyConfig.domain}-loyalty.json`)
            ]) as [IDrupalConfig, ILoyaltyConfig];

            const mergedConfig = mergeConfigurationFiles(
                drupalConfigData.drupal?.dashboard?.loyaltyLandingPageConfig || {},
                loyaltyConfigData?.dashboard?.loyaltyLandingPageConfig || {}
            );

            this.setInitialConfigs(mergedConfig);
        } catch (error) {
            console.error('Failed to load loyalty config:', error);
        }
    }

    @action
    private updateLayout = () => {
        const transformedConfigs = Object.keys(this.configs).reduce((accumulator, key) => {
            if (this.configs[key].params?.[key]?.hideForLoyalty && this.data.isLoyaltyUser) {
                return accumulator;
            }
            accumulator[key] = this.configs[key];

            return accumulator;
        }, {} as LoyaltyLandingConfig);
        this.configs = transformedConfigs;
        this.layouts = createMultiGridLayout(transformedConfigs);
    };

    @action
    setInitialConfigs(configs: LoyaltyLandingConfig) {
        this.configs = configs;
        this.updateLayout();
    }

    @computed
    public get data() {
        return {
            isLoading: this.isLoading,
            isLoyaltyUser: this.loyaltyRepository.isLoyaltyUser,
            isOnlineUser: this.loyaltyRepository.isOnlineUser,
            layouts: this.layouts,
            configs: this.configs,
            hasEligibleBirthdayOffers: this.loyaltyRepository?.hasEligibleBirthdayOffers
        };
    }
}
export default LoyaltyLandingPageController;
